<template>
  <RevCard class="p-24">
    <div class="mb-24 flex items-center justify-between">
      <div class="heading-2 flex items-center gap-8">
        <IconMoney />
        {{ i18n(translations.wallet) }}
      </div>

      <div class="flex items-center gap-24">
        <RevInputSelect
          v-if="currenciesOptions.length && currentCurrency"
          id="currencies"
          v-model="currentCurrency"
          :label="i18n(translations.currency)"
          name="currencies"
          :options="currenciesOptions"
          size="small"
        />

        <RevButton
          size="small"
          :to="{ name: ROUTE_NAMES.INVOICES.WALLET }"
          variant="secondary"
        >
          {{ i18n(translations.viewDetails) }}
        </RevButton>
      </div>
    </div>

    <div>
      <RevLoadingScreen
        v-if="isLoading"
        class="mt-32"
        :text="i18n(translations.loading)"
      />

      <div v-else class="grid grid-cols-2 gap-24">
        <div
          v-if="totalAmount"
          class="border-static-default-mid rounded-sm flex w-full flex-col justify-between gap-12 border p-24"
        >
          <div class="body-1 flex items-start gap-8">
            {{ i18n(translations.totalAmount) }}
          </div>

          <div class="heading-1">
            {{ totalAmount }}
          </div>
        </div>

        <div
          v-if="nextPayout"
          class="border-static-default-mid rounded-sm flex w-full flex-col justify-between gap-12 border p-24"
          data-allow-mismatch
        >
          <div>
            <div class="text-static-default-hi heading-2" data-allow-mismatch>
              {{ i18n(translations.nextPayout, { date: nextPayout.date }) }}
            </div>
            <div class="text-static-default-low" data-allow-mismatch>
              {{ i18n(translations.salesPeriod, { range: nextPayout.range }) }}
            </div>
          </div>

          <div class="text-static-success-hi heading-2 mt-24">
            {{ nextPayout.total }}
          </div>
        </div>

        <div
          v-if="!nextPayout"
          class="border-static-default-mid rounded-sm flex w-full flex-col justify-between gap-12 border p-24"
        >
          <div class="text-static-default-hi heading-2">
            {{ i18n(translations.noUpcomingPayout) }}
          </div>
        </div>
      </div>
    </div>
  </RevCard>
</template>

<script setup lang="ts">
import { type Error, getApiErrorMessage } from '#imports'
import { computed, ref } from 'vue'

import { Currency, merchantsAPI } from '@backmarket/http-api'
import { useHttpFetch } from '@backmarket/nuxt-module-http/useHttpFetch'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useTheToast } from '@backmarket/nuxt-module-toast/useTheToast'
import { RevButton } from '@ds/components/Button'
import { RevCard } from '@ds/components/Card'
import { RevInputSelect } from '@ds/components/InputSelect'
import { RevLoadingScreen } from '@ds/components/LoadingScreen'
import { IconMoney } from '@ds/icons/IconMoney'

import { ROUTE_NAMES } from '~/constants/routes'

import translations from './Wallet.translations'

const i18n = useI18n()
const { openErrorToast } = useTheToast()

const {
  data,
  error,
  pending: isLoading,
} = await useHttpFetch(merchantsAPI.getSellerWallet)

if (error.value) {
  openErrorToast({
    content: getApiErrorMessage(error.value as unknown as Error),
  })
}
const liveWallet = computed(() => data.value?.live_wallet || {})
const upcomingPayouts = computed(() => data.value?.planned_payouts || [])
const currenciesOptions = computed(() => {
  return Object.keys(liveWallet.value).map((currency) => {
    return {
      label: currency,
      value: currency as Currency,
    }
  })
})

const currentCurrency = ref<Currency | undefined>(
  currenciesOptions.value[0]?.value,
)

const totalAmount = computed(() => {
  if (!currentCurrency.value) return null

  const rawTotalAmount = liveWallet.value[currentCurrency.value] || 0

  return i18n.price(rawTotalAmount, {
    currency: currentCurrency.value,
  })
})

const nextPayout = computed(() => {
  const upcomingPayout = upcomingPayouts.value[0]

  if (!upcomingPayout || !currentCurrency.value) return null

  const rawTotalAmount = upcomingPayout.amounts[currentCurrency.value] || 0

  return {
    date: i18n.date(new Date(upcomingPayout.invoicing_date)),
    range: `${i18n.date(new Date(upcomingPayout.period_start))}-${i18n.date(new Date(upcomingPayout.period_end))}`,
    total: i18n.price(rawTotalAmount, {
      currency: currentCurrency.value,
    }),
  }
})
</script>
